/**
 * The least-squares step of true-up's Gauss-Newton solve: plain dense linear
 * algebra, small enough (one row per match) to solve directly.
 */

/** Solves the small dense system `matrix · x = vector` by Gaussian elimination with partial pivoting. */
function solve(matrix: number[][], vector: number[]): number[] {
  const size = vector.length;
  const augmented = matrix.map((row, index) => [...row, vector[index]]);
  for (let column = 0; column < size; column++) {
    let pivot = column;
    for (let row = column + 1; row < size; row++) if (Math.abs(augmented[row][column]) > Math.abs(augmented[pivot][column])) pivot = row;
    [augmented[column], augmented[pivot]] = [augmented[pivot], augmented[column]];
    const pivotValue = augmented[column][column];
    if (Math.abs(pivotValue) < 1e-14) continue;
    for (let row = 0; row < size; row++) {
      if (row === column) continue;
      const factor = augmented[row][column] / pivotValue;
      if (factor) { for (let entry = column; entry <= size; entry++) augmented[row][entry] -= factor * augmented[column][entry]; }
    }
  }
  return augmented.map((row, index) => (Math.abs(row[index]) < 1e-14 ? 0 : row[size] / row[index]));
}

/**
 * One weighted minimum-norm Gauss-Newton step: the smallest correction δ, in
 * the metric Σ(δᵢ/σᵢ)², that drives every residual to zero to first order.
 * δ = −Σ Jᵀ (J Σ Jᵀ + λI)⁻¹ r, with Σ = diag(σᵢ²) and a whisker of damping
 * for matches that say the same thing twice. `jacobian` has a row per
 * residual and a column per variance; a zero variance holds its parameter still.
 */
export function minimumNormStep(jacobian: number[][], residuals: number[], variances: number[]): number[] {
  const matchCount = residuals.length, parameterCount = variances.length;
  const normal = Array.from({ length: matchCount }, (_, row) =>
    Array.from({ length: matchCount }, (_, column) => {
      let sum = row === column ? 1e-12 : 0;
      for (let parameter = 0; parameter < parameterCount; parameter++) sum += jacobian[row][parameter] * variances[parameter] * jacobian[column][parameter];
      return sum;
    }));
  const multipliers = solve(normal, residuals.map(residual => -residual));
  return variances.map((variance, parameter) => variance * multipliers.reduce((sum, multiplier, row) => sum + jacobian[row][parameter] * multiplier, 0));
}
